/* eslint-disable camelcase */
import { Stack, useNavigationContainerRef } from 'expo-router'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { isWeb } from 'tamagui'
import '../src/configs/locales'
import '../src/configs/reactotron'
// Registers the optional Chromecast adapter (native only; .web is a no-op).
import '../src/configs/register-cast'
import '../src/configs/fcm'

import { ApolloProvider } from '@apollo/client'
import { AdminPreviewRouter } from '@components/AdminPreviewRouter'
import { LoadingScreen } from '@components/LoadingScreen'
import { OfflineCoverPage } from '@components/OfflineCoverPage'
import { PushPermissionPrompt } from '@components/Push/PermissionPrompt'
import { RootErrorBoundary } from '@components/RootErrorBoundary'
import { ThemeProvider } from '@components/ThemeProvider'
import { ThemedStatusBar } from '@components/ThemedStatusBar'
import { Toast } from '@components/Toast'
import { navigationIntegration } from '@configs/sentry'
import { NetworkContextProvider } from '@contexts/NetworkContext'
import { OnboardingProvider } from '@contexts/OnboardingContext'
import { SSLProvider } from '@contexts/ScreenLockerLoad'
import { ScrollControlProvider } from '@contexts/ScrollControlContext'
import { SessionProvider } from '@contexts/SessionContext'
import { ThemeParamsProvider } from '@contexts/ThemeParamsContext'
import {
  Mulish_400Regular,
  Mulish_600SemiBold,
  Mulish_700Bold,
} from '@expo-google-fonts/mulish'
import { useAppUpdates } from '@hooks/useAppUpdates'
import { useNetwork } from '@hooks/useNetwork'
import { useNotificationTapHandler } from '@hooks/useNotificationTapHandler'
import * as Sentry from '@sentry/react-native'
import { apolloClient } from '@services/api/apollo'
import { ToastProvider, ToastViewport } from '@tamagui/toast'
import { loadAsync } from 'expo-font'
import { useEffect, useState } from 'react'
import { QueryClientProvider } from 'react-query'
import 'regenerator-runtime/runtime'
import { startHotjar } from 'src/vendors/hotjar'
import { queryClient } from '../src/configs/queryClient'

if (isWeb) {
  document.title = 'Carregando...'
}

function AppContent() {
  const { isConnected } = useNetwork()
  const { isCheckingForUpdate, isDownloading } = useAppUpdates()
  const [fontsLoaded, setFontsLoaded] = useState(false)

  useEffect(() => {
    if (isWeb) {
      setFontsLoaded(true)
      return
    }

    loadAsync({
      Mulish_400Regular,
      Mulish_600SemiBold,
      Mulish_700Bold,
    }).then(() => {
      setFontsLoaded(true)
    })
  }, [])

  if (!fontsLoaded || isCheckingForUpdate || isDownloading) {
    return <LoadingScreen />
  }

  // Na web o browser já comunica offline e o NetInfo dá falso-positivo no
  // load (reachability indeterminada); a cover só existe no app nativo.
  if (!isConnected && !isWeb) {
    return <OfflineCoverPage />
  }

  return (
    <Stack
      screenOptions={{
        headerShown: false,
      }}
    />
  )
}

function PushNotifications() {
  useNotificationTapHandler()

  return <PushPermissionPrompt />
}

function Layout() {
  const ref = useNavigationContainerRef()

  useEffect(() => {
    if (ref) {
      navigationIntegration.registerNavigationContainer(ref)
    }
  }, [ref])

  useEffect(() => {
    startHotjar()
  }, [])

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <NetworkContextProvider>
        <QueryClientProvider client={queryClient}>
          <ApolloProvider client={apolloClient}>
            <ScrollControlProvider>
              <ThemeProvider>
                <ToastProvider>
                  <ThemeParamsProvider>
                    <OnboardingProvider>
                      <SessionProvider>
                        <SSLProvider>
                          <AdminPreviewRouter />
                          <AppContent />
                          <PushNotifications />
                          <ToastViewport
                            $gtSm={{
                              top: 40,
                              right: 40,
                            }}
                          />
                          <Toast />
                          <ThemedStatusBar />
                        </SSLProvider>
                      </SessionProvider>
                    </OnboardingProvider>
                  </ThemeParamsProvider>
                </ToastProvider>
              </ThemeProvider>
            </ScrollControlProvider>
          </ApolloProvider>
        </QueryClientProvider>
      </NetworkContextProvider>
    </GestureHandlerRootView>
  )
}

// O expo-router envolve a rota raiz com este componente, então ele pega
// qualquer erro de render do app inteiro — sem ele, um throw em qualquer tela
// desmonta a árvore React e o aluno fica com a página em branco.
export const ErrorBoundary = RootErrorBoundary

export default Sentry.wrap(Layout)
